import React, { useMemo, useEffect } from 'react';
import { PieChart } from '@/components/ui/chart-wrapper';
import DataTable from './DataTable';
import WidgetBase from './WidgetBase';
import { useWidgetData } from '../../hooks/useWidgetData';
import { exportToCSV } from '../../utils/exportToCSV';
import { getFilterKeyFromColumnHeader, processDashboardUpdate } from '../../utils/processDashboardUpdate';
import { useChartColors } from '../../hooks/useChartColors';

// Maps widget id → localStorage filter key (matches old Column_Pie.js HandleClickOnSlice)
const PIE_FILTER_TYPE_MAP = {
    'WCityTransaction':  'PhCity',
    'WStateTransaction': 'PhState',
    'DStateTransaction': 'PhState',
    'WDrugTransaction':  'DrugName',
};

/**
 * Column Pie Widget Component
 * Modernized replacement for Column_Pie.js
 * Displays data as either column (bar) chart or pie chart with table view
 *
 * API field mapping (matches dev-v3 Column_Pie.js fetchData_locally):
 *   - pieDataSet[0] → used for BOTH the column/bar tab and the pie tab
 *     (same data rendered as different chart types, just like dev-v3)
 *   - listDataSet   → table rows (array of {datasets:[{label,data}]})
 */
function ColumnPieWidget({ data, handlechangea, onLegendUpdate, onExportReady, onDataReady, mode = 'chart' }) {
    const colors = useChartColors();

    // Fetch data using custom hook
    const { data: apiData, loading, error } = useWidgetData(
        data.url,
        data.RequestJson,
        data.RequestJson1
    );

    // Transform pieDataSet[0] → chart data used by BOTH the column tab and the pie tab
    const pieChartData = useMemo(() => {
        if (!apiData || apiData.length === 0) return null;

        try {
            const pieDataset = apiData[0].pieDataSet?.[0];
            if (!pieDataset || !pieDataset.labels || !pieDataset.datasets?.[0]?.data
                || pieDataset.labels.length === 0 || pieDataset.datasets[0].data.length === 0) return null;

            // Process background colors (colour substitution matching dev-v3)
            let bgColors = pieDataset.datasets[0].backgroundColor || [];
            let hoverColors = pieDataset.datasets[0].hoverBackgroundColor || [];

            try {
                // Remap API-returned colors to current theme colors so pie slices
                // stay brand-consistent after a theme change (Tweakcn / dark mode).
                const bgStr = (Array.isArray(bgColors) ? bgColors : [bgColors]).join(',');
                bgColors = bgStr
                    .replace(/#FF8B6C/gi, colors.chart3)   // coral → chart3
                    .replace(/#44B672/gi, colors.chart2)   // green → chart2 (accent)
                    .replace(/#3CB26D/gi, colors.chart2)   // green → chart2 (accent)
                    .replace(/#A6DBBD/gi, '#9fd4fc')       // light-green → sky (no CSS var)
                    .split(',');

                const hvStr = (Array.isArray(hoverColors) ? hoverColors : [hoverColors]).join(',');
                hoverColors = hvStr
                    .replace(/#FF8B6C/gi, colors.chart3)
                    .replace(/#44B672/gi, colors.chart2)
                    .replace(/#3CB26D/gi, colors.chart2)
                    .replace(/#A6DBBD/gi, '#9fd4fc')
                    .split(',');
            } catch (_) {
                // keep original colors if processing fails
            }

            return {
                labels: pieDataset.labels,
                datasets: [{
                    label: data.YlabelString || 'Value',
                    data: pieDataset.datasets[0].data,
                    backgroundColor: bgColors,
                    hoverBackgroundColor: hoverColors,
                    borderWidth: 1
                }]
            };
        } catch (err) {
            console.error('Error transforming pieDataSet:', err);
            return null;
        }
    }, [apiData, data, colors]);

    // Transform listDataSet → table rows (same pattern as TableWidget)
    const { tableData, columns } = useMemo(() => {
        if (!apiData || apiData.length === 0) return { tableData: [], columns: [] };

        try {
            const listDataSet = apiData[0].listDataSet;
            if (!listDataSet || listDataSet.length === 0) return { tableData: [], columns: [] };

            // First row defines column headers (header text is in .data field)
            const cols = (listDataSet[0].datasets || []).map((ds, idx) => ({
                key: `col${idx}`,
                label: ds.data || `Column ${idx + 1}`,
                sortable: true,
                format: (val) => val ?? '-'
            }));

            // Data rows start at index 1 (index 0 is the header row)
            const rows = listDataSet.slice(1).map(item => {
                const row = {};
                (item.datasets || []).forEach((ds, idx) => {
                    row[`col${idx}`] = ds.data;
                });
                return row;
            });

            return { tableData: rows, columns: cols };
        } catch (err) {
            console.error('Error transforming listDataSet:', err);
            return { tableData: [], columns: [] };
        }
    }, [apiData]);

    // Notify parent whether this widget has data (used to disable toggle / export buttons).
    // Use derived pieChartData/tableData — raw apiData can be non-empty with no real content.
    useEffect(() => {
        if (loading) return;
        if (onDataReady) onDataReady(pieChartData !== null || tableData.length > 0);
    }, [loading, pieChartData, tableData]); // eslint-disable-line react-hooks/exhaustive-deps

    // Report legend value to parent (matching old Column_Pie.js widget-head number)
    useEffect(() => {
        if (!apiData || apiData.length === 0 || !onLegendUpdate) return;
        try {
            const legendVal = apiData[0].legend?.[0]?.legendValue ?? '';
            if (legendVal) onLegendUpdate(data.id, String(legendVal));
        } catch (e) { /* ignore */ }
    }, [apiData]);

    // Export handler
    const handleExport = () => {
        if (tableData.length > 0) {
            exportToCSV(tableData, data.title || 'chart-data', columns);
        }
    };

    // Register export handler with parent (for header-level export button)
    useEffect(() => {
        if (onExportReady) onExportReady(handleExport);
    }, [tableData]);

    // Resolve filter type from widget id (matches old Column_Pie.js HandleClickOnSlice mapping)
    const filterType = PIE_FILTER_TYPE_MAP[data.id] || null;

    // Per-cell click handler — resolves the filter key from each cell's column label so that
    // multiple columns in the same row (e.g. "City Name" + "State") can each add their own filter.
    const handleCellClick = (row, columnKey, columnLabel) => {
        if (!handlechangea) return;
        // Resolve from column header first, then fall back to PIE_FILTER_TYPE_MAP for the widget
        const resolvedFilterKey = getFilterKeyFromColumnHeader(columnLabel) || filterType;
        if (!resolvedFilterKey) return;
        let value = row[columnKey];
        if (!value || String(value) === 'Total') return;
        // For city values strip the suffix after a comma (e.g. "DALLAS, TX" → "DALLAS")
        if (resolvedFilterKey === 'PhCity') value = String(value).split(',')[0].trim();
        processDashboardUpdate(resolvedFilterKey, String(value), () => handlechangea && handlechangea());
    };

    // Chart component — pie chart only
    const chartComponent = pieChartData ? (
        <PieChart
            data={pieChartData}
                        height={280}
            options={{
                onClick: (event, elements) => {
                    if (elements.length > 0 && filterType) {
                        const index = elements[0].index;
                        // For city labels the old code strips suffix after comma (e.g. "DALLAS, TX" → "DALLAS")
                        let label = pieChartData.labels[index];
                        if (filterType === 'PhCity') label = label.split(',')[0];
                        // Write filter tag to localStorage then trigger dashboard refresh
                        processDashboardUpdate(filterType, label, () => handlechangea && handlechangea());
                    }
                }
            }}
        />
    ) : null;

    return (
        <WidgetBase
            loading={loading}
            error={error}
            chartComponent={chartComponent}
            tableComponent={
                tableData.length > 0 ? (
                    <DataTable
                        columns={columns}
                        data={tableData}
                        onCellClick={handlechangea ? handleCellClick : null}
                        pagination={{ enabled: true, itemsPerPage: 6 }}
                        sortable={true}
                        searchable={false}
                    />
                ) : (
                    <div className="flex items-center justify-center py-12">
                        <p className="text-primary/70">No Data Available</p>
                    </div>
                )
            }
            mode={mode}
        />
    );
}

export default ColumnPieWidget;
